/**
 * MobileTrainingTab.jsx
 * Training log for the mobile NutritionScreen
 * Shows booked trainings, manual entries, and afterburn/EPOC
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Dumbbell, Flame, Plus, Clock, Trash2, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ACTIVITY_TYPES = [
  { id: 'strength', icon: '💪', defaultCal: 350 },
  { id: 'cardio', icon: '🏃', defaultCal: 400 },
  { id: 'hiit', icon: '⚡', defaultCal: 500 },
  { id: 'yoga', icon: '🧘', defaultCal: 150 },
  { id: 'swimming', icon: '🏊', defaultCal: 450 },
  { id: 'cycling', icon: '🚴', defaultCal: 350 },
  { id: 'walking', icon: '🚶', defaultCal: 200 },
  { id: 'other', icon: '🎯', defaultCal: 250 },
];

const MobileTrainingTab = ({ portalToken, selectedDate }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [trainingLog, setTrainingLog] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // Add form state
  const [selectedType, setSelectedType] = useState('strength');
  const [duration, setDuration] = useState('60');
  const [calories, setCalories] = useState('350');
  const [activityName, setActivityName] = useState('');

  useEffect(() => {
    loadTrainingLog();
  }, [portalToken, selectedDate]);

  const loadTrainingLog = async () => {
    if (!portalToken) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/portal/nutrition/training-log/today`, {
        params: { date: selectedDate },
        headers: { Authorization: `Bearer ${portalToken}` }
      });
      setTrainingLog(response.data);
    } catch (error) {
      console.error('Error loading training log:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddTraining = async () => {
    if (!portalToken) return;
    setSaving(true);
    try {
      await axios.post(`${API}/portal/nutrition/training-entry`, {
        activity_type: selectedType,
        activity_name: activityName || t(`training.types.${selectedType}`),
        duration_minutes: parseInt(duration) || 60,
        calories_burned: parseInt(calories) || 0,
        date: selectedDate
      }, { headers: { Authorization: `Bearer ${portalToken}` } });

      toast.success(t('training.added'));
      setShowAddForm(false);
      setActivityName('');
      setDuration('60');
      setCalories('350');
      loadTrainingLog();
    } catch (error) {
      toast.error(t('training.save_error'));
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteEntry = async (entryId) => {
    if (!portalToken) return;
    setDeletingId(entryId);
    try {
      await axios.delete(`${API}/portal/nutrition/training-entry/${entryId}`, {
        headers: { Authorization: `Bearer ${portalToken}` }
      });
      toast.success(t('training.removed'));
      loadTrainingLog();
    } catch (error) {
      toast.error(t('training.delete_error'));
    } finally {
      setDeletingId(null);
    }
  };

  const handleTypeChange = (typeId) => {
    setSelectedType(typeId);
    const type = ACTIVITY_TYPES.find(a => a.id === typeId);
    if (type) setCalories(String(type.defaultCal));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-8 h-8 text-yellow-500 animate-spin" />
      </div>
    );
  }

  const bookedTrainings = trainingLog?.booked_trainings || [];
  const manualEntries = trainingLog?.manual_entries || [];
  const totalCalories = trainingLog?.total_calories_burned || 0;
  const afterburn = trainingLog?.afterburn_calories || 0;

  return (
    <div className="p-4 space-y-4 pb-32" data-testid="mobile-training-tab">
      {/* Summary Card */}
      <div className="bg-gradient-to-br from-orange-500/20 to-orange-600/10 rounded-2xl p-4 border border-orange-500/30">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Dumbbell className="w-5 h-5 text-orange-400" />
            <span className="text-white font-semibold">{t('training.title')}</span>
          </div>
          <span className="text-orange-400 font-bold text-lg">{totalCalories} kcal</span>
        </div>
        {afterburn > 0 && (
          <div className="flex items-center gap-2 p-2 bg-orange-500/10 rounded-xl border border-orange-500/20 mt-2">
            <Flame className="w-4 h-4 text-orange-400" />
            <span className="text-orange-300 text-xs">{t('training.afterburn')}: +{afterburn} kcal</span>
          </div>
        )}
      </div>

      {/* Booked Trainings */}
      {bookedTrainings.length > 0 && (
        <div className="space-y-2">
          <p className="text-zinc-400 text-xs font-medium uppercase tracking-wider">{t('training.booked')}</p>
          {bookedTrainings.map((t, idx) => (
            <div key={idx} className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-xl border border-zinc-700/50">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-green-500/20 rounded-lg flex items-center justify-center">
                  <Dumbbell className="w-4 h-4 text-green-400" />
                </div>
                <div>
                  <p className="text-white text-sm font-medium">{t.service_name}</p>
                  <p className="text-zinc-500 text-xs flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {t.duration_minutes || 60} Min
                  </p>
                </div>
              </div>
              <span className="text-orange-400 text-sm font-medium">+{t.calories_burned || 0} kcal</span>
            </div>
          ))}
        </div>
      )}

      {/* Manual Entries */}
      {manualEntries.length > 0 && (
        <div className="space-y-2">
          <p className="text-zinc-400 text-xs font-medium uppercase tracking-wider">{t('training.manual')}</p>
          {manualEntries.map((e) => (
            <div key={e.id} className="flex items-center justify-between p-3 bg-zinc-800/50 rounded-xl border border-zinc-700/50">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 bg-blue-500/20 rounded-lg flex items-center justify-center">
                  <span className="text-sm">
                    {ACTIVITY_TYPES.find(a => a.id === e.activity_type)?.icon || '🎯'}
                  </span>
                </div>
                <div>
                  <p className="text-white text-sm font-medium">{e.activity_name || e.activity_type}</p>
                  <p className="text-zinc-500 text-xs flex items-center gap-1">
                    <Clock className="w-3 h-3" /> {e.duration_minutes || 0} Min
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-orange-400 text-sm font-medium">+{e.estimated_calories || e.calories_burned || 0} kcal</span>
                <button
                  onClick={() => handleDeleteEntry(e.id)}
                  className="p-1.5 text-zinc-500 hover:text-red-400 transition-colors"
                  data-testid={`delete-training-${e.id}`}
                >
                  {deletingId === e.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4" />
                  )}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty State */}
      {bookedTrainings.length === 0 && manualEntries.length === 0 && (
        <div className="text-center py-8">
          <Dumbbell className="w-12 h-12 text-zinc-700 mx-auto mb-3" />
          <p className="text-zinc-500 text-sm">{t('training.no_training')}</p>
          <p className="text-zinc-600 text-xs mt-1">{t('training.add_hint')}</p>
        </div>
      )}

      {/* Add Training Button */}
      {!showAddForm && (
        <button
          onClick={() => setShowAddForm(true)}
          className="w-full flex items-center justify-center gap-2 p-3 bg-orange-500/10 border border-orange-500/30 rounded-xl text-orange-400 text-sm font-medium hover:bg-orange-500/20 transition-colors"
          data-testid="add-training-btn"
        >
          <Plus className="w-4 h-4" /> {t('training.add_button')}
        </button>
      )}

      {/* Add Training Form */}
      {showAddForm && (
        <div className="bg-zinc-800/70 rounded-2xl p-4 border border-zinc-700 space-y-3" data-testid="add-training-form">
          <p className="text-white text-sm font-semibold">{t('training.add_button')}</p>

          {/* Activity Type Grid */}
          <div className="grid grid-cols-4 gap-2">
            {ACTIVITY_TYPES.map(type => (
              <button
                key={type.id}
                onClick={() => handleTypeChange(type.id)}
                className={`flex flex-col items-center gap-1 p-2 rounded-xl text-center transition-colors ${
                  selectedType === type.id
                    ? 'bg-yellow-500/20 border border-yellow-500/50'
                    : 'bg-zinc-700/50 border border-zinc-700'
                }`}
              >
                <span className="text-lg">{type.icon}</span>
                <span className={`text-[9px] ${selectedType === type.id ? 'text-yellow-400' : 'text-zinc-400'}`}>
                  {t(`training.types.${type.id}`)}
                </span>
              </button>
            ))}
          </div>

          {/* Name (optional) */}
          <input
            type="text"
            value={activityName}
            onChange={(e) => setActivityName(e.target.value)}
            placeholder={`${t('training.label')} (${t('common.optional')})`}
            className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-3 py-2 text-white text-sm placeholder-zinc-500"
          />

          {/* Duration & Calories */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-zinc-400 text-xs mb-1 block">{t('training.duration')}</label>
              <input
                type="number"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-3 py-2 text-white text-sm"
              />
            </div>
            <div>
              <label className="text-zinc-400 text-xs mb-1 block">{t('training.calories_label')}</label>
              <input
                type="number"
                value={calories}
                onChange={(e) => setCalories(e.target.value)}
                className="w-full bg-zinc-700 border border-zinc-600 rounded-xl px-3 py-2 text-white text-sm"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <button
              onClick={() => setShowAddForm(false)}
              className="flex-1 py-2 bg-zinc-700 text-zinc-300 rounded-xl text-sm"
            >
              {t('common.cancel')}
            </button>
            <button
              onClick={handleAddTraining}
              disabled={saving}
              className="flex-1 py-2 bg-yellow-500 text-black rounded-xl text-sm font-medium disabled:opacity-50"
              data-testid="save-training-btn"
            >
              {saving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default MobileTrainingTab;
